import React, { useCallback, useEffect } from 'react';
import { cn } from '@/lib/utils';

export interface ResizableImageWrapperProps {
  src: string;
  alt?: string;
  title?: string;
  width?: number;
  height?: number;
  hasBeenResized?: boolean;
  selected: boolean;
  isDragging: boolean;
  imageLoaded: boolean;
  maxWidth?: number;
  onResizeStart: (startX: number, startY: number) => void;
  onImageLoad: () => void;
  onImageRef: (ref: HTMLImageElement | null) => void;
}

/**
 * ResizableImageWrapper Component
 * 
 * Renders an image with resize handles and white border when selected.
 * Handles drag operations for resizing while maintaining aspect ratio.
 * Uses responsive styling by default, only applying explicit widths after manual resize.
 */
export const ResizableImageWrapper: React.FC<ResizableImageWrapperProps> = ({
  src,
  alt,
  title,
  width,
  height,
  hasBeenResized = false,
  selected,
  isDragging,
  imageLoaded,
  maxWidth,
  onResizeStart,
  onImageLoad,
  onImageRef,
}) => {
  // Debug logging for selection and state changes
  useEffect(() => {
    console.log('[ResizableImageWrapper] State changed:', {
      selected,
      imageLoaded,
      isDragging,
      width,
      height,
      hasBeenResized,
      src: src.substring(0, 50) + '...',
      timestamp: new Date().toISOString()
    });
  }, [selected, imageLoaded, isDragging, width, height, hasBeenResized, src]);

  // Handle resize handle mouse down
  const handleResizeMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation(); // Prevent image drag when interacting with resize handles
    
    console.log('[ResizableImageWrapper] Resize handle mouse down:', {
      clientX: e.clientX,
      clientY: e.clientY,
      button: e.button
    });
    
    onResizeStart(e.clientX, e.clientY);
  }, [onResizeStart]);

  // Handle drag start - override native behavior to enable move instead of copy
  const handleDragStart = useCallback((e: React.DragEvent) => {
    console.log('[ResizableImageWrapper] Drag start - setting up move operation');
    
    // Set drag effect to move instead of copy
    e.dataTransfer.effectAllowed = 'move';
    
    // Store image source and position info for the drop handler
    e.dataTransfer.setData('text/html', e.currentTarget.outerHTML);
    e.dataTransfer.setData('application/x-tiptap-image', JSON.stringify({
      src,
      alt,
      title,
      width,
      height,
      hasBeenResized
    }));
    
    // Add visual feedback
    (e.currentTarget as HTMLElement).style.opacity = '0.5';
  }, [src, alt, title, width, height, hasBeenResized]);

  // Handle drag end - clean up visual feedback
  const handleDragEnd = useCallback((e: React.DragEvent) => {
    console.log('[ResizableImageWrapper] Drag end - cleaning up');
    (e.currentTarget as HTMLElement).style.opacity = '1';
  }, []);

  // Determine styling approach: responsive by default, explicit only after manual resize
  const useExplicitDimensions = hasBeenResized && width && height;
  
  // Calculate display dimensions
  const shouldConstrainWidth = useExplicitDimensions && maxWidth && width > maxWidth;
  const displayWidth = useExplicitDimensions 
    ? (shouldConstrainWidth ? '100%' : `${width}px`)
    : '100%';
  const displayHeight = useExplicitDimensions 
    ? (shouldConstrainWidth ? 'auto' : `${height}px`) 
    : 'auto';

  // Container styling: responsive by default, bulletproof max-width
  const containerStyle = useExplicitDimensions ? {
    width: displayWidth,
    maxWidth: '100%', // ✅ Bulletproof - never exceed container
  } : {
    maxWidth: '100%', // ✅ Bulletproof - never exceed container
  };

  // Image styling: responsive by default, bulletproof max-width  
  const imageStyle = useExplicitDimensions ? {
    width: displayWidth,
    height: displayHeight,
    maxWidth: '100%', // ✅ Bulletproof - never exceed container
  } : {
    width: '100%',
    height: 'auto',
    maxWidth: '100%', // ✅ Bulletproof - never exceed container
  };

  return (
    <div 
      className={cn(
        "relative inline-block group",
        "resizable-image-container",
        selected && "resizable-image-selected",
        isDragging && "resizable-image-dragging"
      )}
      style={containerStyle}
    >
      {/* Image Element */}
      <img
        ref={onImageRef}
        src={src}
        alt={alt}
        title={title}
        onLoad={onImageLoad}
        className={cn(
          "resizable-image-element",
          "rounded-md",
          "block",
          useExplicitDimensions ? "" : "w-full h-auto",
          selected && "ring-2 ring-white ring-offset-2 ring-offset-gray-100",
          // Removed: isDragging && "pointer-events-none select-none" 
          // This was preventing TipTap position dragging while resize dragging was active
          !imageLoaded && "opacity-50"
        )}
        style={imageStyle}
        draggable={selected && imageLoaded}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      />

      {/* Resize Handles - Only show when selected and not dragging from other handles */}
      {selected && imageLoaded && (
        <>
          {/* Corner resize handles */}
          <div
            className={cn(
              "resize-handle cursor-se-resize",
              "absolute -bottom-1 -right-1"
            )}
            onMouseDown={handleResizeMouseDown}
          />
          
          {/* Bottom center handle */}
          <div
            className={cn(
              "resize-handle cursor-s-resize",
              "absolute -bottom-1 left-1/2 transform -translate-x-1/2"
            )}
            onMouseDown={handleResizeMouseDown}
          />
          
          {/* Right center handle */}
          <div
            className={cn(
              "resize-handle cursor-e-resize",
              "absolute -right-1 top-1/2 transform -translate-y-1/2"
            )}
            onMouseDown={handleResizeMouseDown}
          />

          {/* Top right handle */}
          <div
            className={cn(
              "resize-handle cursor-ne-resize",
              "absolute -top-1 -right-1"
            )}
            onMouseDown={handleResizeMouseDown}
          />

          {/* Top center handle */}
          <div
            className={cn(
              "resize-handle cursor-n-resize",
              "absolute -top-1 left-1/2 transform -translate-x-1/2"
            )}
            onMouseDown={handleResizeMouseDown}
          />

          {/* Top left handle */}
          <div
            className={cn(
              "resize-handle cursor-nw-resize",
              "absolute -top-1 -left-1"
            )}
            onMouseDown={handleResizeMouseDown}
          />

          {/* Left center handle */}
          <div
            className={cn(
              "resize-handle cursor-w-resize",
              "absolute -left-1 top-1/2 transform -translate-y-1/2"
            )}
            onMouseDown={handleResizeMouseDown}
          />

          {/* Bottom left handle */}
          <div
            className={cn(
              "resize-handle cursor-sw-resize",
              "absolute -bottom-1 -left-1"
            )}
            onMouseDown={handleResizeMouseDown}
          />
        </>
      )}

      {/* Dragging overlay */}
      {isDragging && (
        <div className="absolute inset-0 bg-black bg-opacity-10 pointer-events-none" />
      )}
    </div>
  );
}; 